import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Toast from 'react-bootstrap/Toast';

import Icon from '../../../../components/icon/Icon';
import { GlobalNotification as GlobalNotificationModel } from '../../../../core/state/global-notifications/types';
import { Actions as NotificationActions } from '../../../../core/state/global-notifications/actions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { isMobileScreenSize } from '../../../../core/utils/isMobileScreenSize';

import './GlobalNotification.scss';

interface Props {
  notification: GlobalNotificationModel;
}

const GlobalNotification: React.FC<Props> = ({ notification }) => {
  const { t } = useTranslation();
  const isMobileScreen = isMobileScreenSize();

  const { type, message, autoHide, delay } = notification;
  const dispatch = useDispatch();
  const dismiss = () => dispatch(NotificationActions.dismissGlobalNotification(notification));

  let icon;
  let title;
  switch (type) {
    case GlobalNotificationTypes.Success:
      icon = <Icon className="global-notification__status-icon" icon="correct" svg size="small" />;
      title = notification.title || t('toast.success.title');
      break;
    case GlobalNotificationTypes.Error:
      icon = <Icon className="global-notification__status-icon" icon="alert" svg size="small" />;
      title = notification.title || t('toast.error.title');
      break;
    case GlobalNotificationTypes.Info:
      icon = <Icon className="global-notification__status-icon" icon="info" svg size="small" />;
      title = notification.title || t('toast.info.title');
      break;
    case GlobalNotificationTypes.Warning:
      icon = <Icon className="global-notification__status-icon" icon="info" svg size="small" />;
      title = notification.title || t('toast.warning.title');
      break;

    default:
      icon = undefined;
  }

  if (autoHide !== false) {
    setTimeout(dismiss, delay);
  }

  return (
    <Toast
      className={`global-notification global-notification--${notification.type} ${
        isMobileScreen ? 'global-notification--mobile' : ''
      } global-notification--${notification.postition?.toLowerCase()}`}
    >
      <Toast.Body className="global-notification__body">
        {icon}
        <div className="global-notification__content">
          <span className="global-notification__title">{title}</span>
          {message}
        </div>
        {notification.buttons && (
          <div className="global-notification__buttons">
            <button
              onClick={() => {
                if (notification.buttons?.confirm?.onClick) {
                  notification.buttons?.confirm?.onClick();
                }
                dismiss();
              }}
              className="global-notification__button"
            >
              {notification.buttons?.confirm?.text}
            </button>
            <button
              onClick={() => {
                if (notification.buttons?.cancel?.onClick) {
                  notification.buttons?.cancel?.onClick();
                }
                dismiss();
              }}
              className="global-notification__button"
            >
              {notification.buttons?.cancel?.text}
            </button>
          </div>
        )}
        {notification.closeButton !== false && (
          <button className="global-notification__close close" type="button" data-dismiss="toast" onClick={dismiss}>
            <span aria-hidden="true">
              <Icon className="global-notification__close-icon" icon="x" size="extra-small" svg />
            </span>
            <span className="sr-only">{t('common.close')}</span>
          </button>
        )}
      </Toast.Body>
    </Toast>
  );
};

export default GlobalNotification;
